import PropTypes from 'prop-types';
import { Users, ChevronRight } from 'lucide-react';
import { Text, Stack } from '@/components/ui/index.js';
import styles from './ParticipantList.module.css';

/**
 * ParticipantList - Displays event participants with avatar stack
 * Shows overlapping avatars with count and view all option
 */
export function ParticipantList({
  participants,
  maxVisible = 5,
  onViewAll,
  friendsAttending = 0,
}) {
  const { count, max, list } = participants;
  const visibleParticipants = list.slice(0, maxVisible);
  const remainingCount = Math.max(0, count - maxVisible);
  const isFull = count >= max;

  const getInitials = (name) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <div className={styles.participantList}>
      <div className={styles.header}>
        <Stack direction="row" gap={2} align="center">
          <Users size={18} className={styles.headerIcon} />
          <Text size="base" weight="semibold" color="primary">
            Participants ({count}/{max})
          </Text>
        </Stack>

        {friendsAttending > 0 && (
          <div className={styles.friendsBadge}>
            <Text size="xs" color="secondary">
              {friendsAttending} friend{friendsAttending > 1 ? 's' : ''} attending
            </Text>
          </div>
        )}
      </div>

      <div className={styles.content}>
        <div className={styles.avatarStack}>
          {visibleParticipants.map((participant, index) => (
            <div
              key={participant.id}
              className={styles.avatar}
              style={{ zIndex: visibleParticipants.length - index }}
              title={participant.name}
            >
              {participant.avatar ? (
                <img
                  src={participant.avatar}
                  alt={participant.name}
                  className={styles.avatarImage}
                />
              ) : (
                <span className={styles.avatarInitials}>
                  {getInitials(participant.name)}
                </span>
              )}
            </div>
          ))}

          {remainingCount > 0 && (
            <div className={styles.remainingAvatar}>
              <Text size="sm" weight="medium" color="primary">
                +{remainingCount}
              </Text>
            </div>
          )}
        </div>

        {onViewAll && (
          <button
            className={styles.viewAllButton}
            onClick={onViewAll}
            aria-label="View all participants"
          >
            <Text size="sm" color="cyan">
              View All
            </Text>
            <ChevronRight size={16} className={styles.chevronIcon} />
          </button>
        )}
      </div>

      {isFull && (
        <div className={styles.fullBadge}>
          <Text size="xs" weight="medium" color="secondary">
            This event is full
          </Text>
        </div>
      )}

      {count === 1 && (
        <Text size="sm" color="muted" className={styles.beFirst}>
          Be the first to join!
        </Text>
      )}
    </div>
  );
}

ParticipantList.propTypes = {
  participants: PropTypes.shape({
    count: PropTypes.number.isRequired,
    max: PropTypes.number.isRequired,
    list: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        name: PropTypes.string.isRequired,
        avatar: PropTypes.string,
      })
    ).isRequired,
  }).isRequired,
  maxVisible: PropTypes.number,
  onViewAll: PropTypes.func,
  friendsAttending: PropTypes.number,
};

export default ParticipantList;
